import React, { useState } from 'react';
import axios from 'axios';

const EmailVerificationBanner = ({ user }) => {
  const [sending, setSending] = useState(false);
  const [message, setMessage] = useState('');
  const [dismissed, setDismissed] = useState(false);

  // Don't show if email is verified or banner is dismissed
  if (user?.emailVerified || dismissed) {
    return null;
  }

  // Calculate remaining requests before email verification required
  const FREE_UNVERIFIED_LIMIT = 10;
  const apiUsage = user?.apiUsage || 0;
  const remainingRequests = Math.max(0, FREE_UNVERIFIED_LIMIT - apiUsage);
  const isNearLimit = remainingRequests <= 3;
  const isAtLimit = remainingRequests === 0;

  const handleResendEmail = async () => {
    setSending(true);
    setMessage('');

    try {
      const token = localStorage.getItem('token');
      await axios.post(
        `${process.env.REACT_APP_API_URL || 'http://localhost:3000'}/api/auth/resend-verification`,
        {},
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      setMessage('Verification email sent! Please check your inbox.');
      setTimeout(() => setMessage(''), 5000);
    } catch (error) {
      setMessage(
        error.response?.data?.error || 'Failed to send verification email. Please try again.'
      );
    } finally {
      setSending(false);
    }
  };

  return (
    <div className={`border-l-4 p-4 mb-6 ${
      isAtLimit
        ? 'bg-red-50 border-red-400'
        : isNearLimit
        ? 'bg-orange-50 border-orange-400'
        : 'bg-yellow-50 border-yellow-400'
    }`}>
      <div className="flex">
        <div className="flex-shrink-0">
          <svg
            className={`h-5 w-5 ${
              isAtLimit
                ? 'text-red-400'
                : isNearLimit
                ? 'text-orange-400'
                : 'text-yellow-400'
            }`}
            fill="currentColor"
            viewBox="0 0 20 20"
          >
            <path
              fillRule="evenodd"
              d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z"
              clipRule="evenodd"
            />
          </svg>
        </div>
        <div className="ml-3 flex-1">
          {isAtLimit ? (
            <p className="text-sm text-red-700 font-semibold">
              Email verification required! You get a free 100 requests per month, but can only use {FREE_UNVERIFIED_LIMIT} without verifying your email.
              Verify your email to unlock the remaining 90 requests.
            </p>
          ) : (
            <p className={`text-sm ${
              isNearLimit ? 'text-orange-700' : 'text-yellow-700'
            }`}>
              Your email address is not verified. You get a free 100 requests per month, but can only use {FREE_UNVERIFIED_LIMIT} without verification.
              You have <strong>{remainingRequests}</strong> request{remainingRequests !== 1 ? 's' : ''} remaining before email verification is required.
            </p>
          )}
          {message && (
            <p className={`mt-2 text-sm ${message.includes('sent') ? 'text-green-700' : 'text-red-700'}`}>
              {message}
            </p>
          )}
          <div className="mt-3 flex space-x-3">
            <button
              onClick={handleResendEmail}
              disabled={sending}
              className={`text-sm font-medium disabled:opacity-50 ${
                isAtLimit
                  ? 'text-red-700 hover:text-red-600'
                  : isNearLimit
                  ? 'text-orange-700 hover:text-orange-600'
                  : 'text-yellow-700 hover:text-yellow-600'
              }`}
            >
              {sending ? 'Sending...' : 'Resend verification email'}
            </button>
            {!isAtLimit && (
              <button
                onClick={() => setDismissed(true)}
                className={`text-sm font-medium ${
                  isNearLimit
                    ? 'text-orange-700 hover:text-orange-600'
                    : 'text-yellow-700 hover:text-yellow-600'
                }`}
              >
                Dismiss
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default EmailVerificationBanner;
